/* eslint-disable max-len, padded-blocks */
/* This mixin provides methods for getting form schemas */
import api from '@/api';

export default {
  methods: {
    validateRangeNumber(value, minValue, maxValue) {
      // https://stackoverflow.com/questions/12702030/regular-expression-for-3-decimal-point
      if (value === null || Number.isNaN(value)) return ['Must enter a numeric value!'];
      if (value < 0) return [`Cannot be less than ${minValue}`];
      if (value > maxValue) return [`Cannot exceed ${maxValue}!`];
      if (value.toString().match(/\.[0-9]{3}/)) return ['No more than two decimal places allowed!'];
      return [];
    },
    validatePositiveNumber(value, maxValue) {
      // https://stackoverflow.com/questions/12702030/regular-expression-for-3-decimal-point
      if (value === null || Number.isNaN(value)) return ['Must enter a numeric value!'];
      if (value <= 0) return ['Positive non-zero numbers only!'];
      if (value > maxValue) return [`Cannot exceed ${maxValue}!`];
      if (value.toString().match(/\.[0-9]{3}/)) return ['No more than two decimal places allowed!'];
      return [];
    },
    validatePositiveWholeNumber(value, maxValue) {
      // https://stackoverflow.com/questions/12702030/regular-expression-for-3-decimal-point
      if (value === null || Number.isNaN(value)) return ['Must enter a numeric value!'];
      if (value < 0) return ['Non-negative numbers only!'];
      if (value > maxValue) return [`Cannot exceed ${maxValue}!`];
      if (value.toString().match(/\.[0-9]{1}/)) return ['No decimal places allowed!'];
      return [];
    },
    // project budget adj must be at least the amount the annual budget adj
    // exceeds the amount left on the project.
    // It cannot exceed the annual budget adj.
    validateProjectBudgetAdjustment(projectAdjustment) {
      if (projectAdjustment === null || Number.isNaN(projectAdjustment)) return ['Must enter a numeric value!'];
      if (this.fundingSource === undefined) return [];

      if (['ECREP Project', 'Invasive Treatment Project', 'VEGBAC Project', 'Conservation Easement'].includes(this.project.category)) {
        return [];
      }

      const fundingSourceId = this.fundingSources.find((e) => e.recorD_NAME === this.fundingSource).recorD_ID;
      const previouslySpent = this.getPreviousFiscalYearsTotalSpent(this.budgetYear.year, fundingSourceId);
      const previouslyApproved = this.getFiscalYearTotalApproved(this.budgetYear.year, fundingSourceId);

      const projectBudgetLineItemSnapshots = this.projectSnapshotDraft.projectBudgetYearSnapshots
        .map((e) => e.projectBudgetLineItemSnapshots)
        .flat()
        .filter((e) => e.fundingSourceId === fundingSourceId);

      let snapshotTotal = 0;
      projectBudgetLineItemSnapshots.forEach((e) => {
        const baseCost = ((e.quantity * e.unitCost) * (e.costShare * 0.01));
        const contingency = baseCost * e.contingencyPercent * 0.01;
        snapshotTotal += (baseCost + contingency);
      });

      // Calculate increase/decrease totals scoped to the selected funding source only
      // (not the global totals across all funding sources, which would incorrectly
      // penalize this funding source for decreases applied to other funding sources)
      const fundingSourceIncrease = this.projectBudget.projectBudgetYears
        .flatMap((pby) => pby.annualBudgetIncreases)
        .filter((ba) => ba.fundingSourceId === fundingSourceId)
        .map((ba) => ba.adjustmentToProjectBudget)
        .reduce((a, b) => a + b, 0.0);

      const fundingSourceDecrease = (this.projectBudget.projectBudgetDecreases || [])
        .filter((ba) => ba.fundingSourceId === fundingSourceId)
        .map((ba) => ba.decreaseAmount)
        .reduce((a, b) => a + b, 0.0);

      const amountLeft = Number((snapshotTotal + fundingSourceIncrease - fundingSourceDecrease - previouslySpent - previouslyApproved).toFixed(2));
      const annualAdjustment = Number((this.currentAdjustment.adjustmentToAnnualBudget).toFixed(2));
      const localProjectAdjustment = Number((projectAdjustment).toFixed(2));
      let errorArray = [];

      if (!amountLeft && localProjectAdjustment !== annualAdjustment) {
        errorArray.push(`Project Budget Adjustment must be ${annualAdjustment} to provide enough funds for the Annual Budget Adjustment`);
      } else {
        if (amountLeft <= annualAdjustment) {
          const minimumProjectAdjustment = Number((annualAdjustment - amountLeft).toFixed(2));
          if (localProjectAdjustment < minimumProjectAdjustment) {
            errorArray.push(`Project Budget Adjustment must be at least ${this.$options.filters.toCurrency(minimumProjectAdjustment)} to provide enough funds for the Annual Budget Adjustment`);
          }
        } else {
          errorArray = errorArray.concat(this.validateRangeNumber(localProjectAdjustment, 0, 1000000));
        }
        if (localProjectAdjustment > annualAdjustment) {
          errorArray.push('Project Budget Adjustment cannot exceed the Annual Budget Adjustment.');
        }
      }

      return errorArray;
    },
    getProjectDecreaseSchema() {
      return {
        id: 'budgetDecrease',
        legend: 'Decrease Project Budget',
        headerName: 'Decrease Project Budget',
        fields: [
          {
            type: 'select',
            label: '<span class="label-name">Funding Source</span><br/><span class="help-text">Pick the expected Funding Source for this item</span>',
            model: 'fundingSourceId',
            required: true,
            styleClasses: ['field-label-override-font', 'row', 'full-width-label'],
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            values: this.orderedFundingSourcesForCurrentBudget(),
            validator: ['required'],
          },
          {
            type: 'input',
            inputType: 'number',
            label: '<span class="label-name">Decrease Amount</span><br/><span class="help-text"></span>',
            min: 0.01,
            max: 1000000,
            required: true,
            step: '0.01',
            styleClasses: ['field-label-override-font', 'row', 'full-width-label'],
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            model: 'decreaseAmount',
            validator: (value) => (this.validatePositiveNumber(value, 1000000)),
          },
        ],
      };
    },
    getAnnualBudgetIncreaseSchema() {
      return {
        id: 'annualBudgetIncrease',
        legend: 'Adjustment',
        headerName: 'Adjustment',
        fields: [
          {
            type: 'select',
            label: '<span class="label-name">Funding Source</span><br/><span class="help-text">Pick the expected Funding Source for this item</span>',
            model: 'fundingSourceId',
            required: true,
            styleClasses: ['field-label-override-font', 'row', 'full-width-label'],
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            values: this.orderedFundingSources(),
            validator: ['required'],
          },
          {
            type: 'input',
            inputType: 'number',
            label: '<span class="label-name">Change to Annual Budget</span><br/><span class="help-text"></span>',
            min: 0.01,
            max: 1000000,
            required: true,
            step: '0.01',
            styleClasses: ['field-label-override-font', 'row', 'full-width-label'],
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            model: 'adjustmentToAnnualBudget',
            validator: (value) => (this.validatePositiveNumber(value, 1000000)),
          },
          {
            type: 'input',
            inputType: 'number',
            label: '<span class="label-name">Change to Project Budget</span><br/><span class="help-text">Indicate by how much this FY budget change will increase the overall Project Budget. The amount can be the same as the amount of this FY increase. However, if this change is to approve earlier spending of this Project\'s existing budget (shifting from out years to this year), then you can indicate a lesser increase to the overall Project budget, depending on funds remaining for the Project.</span>',
            min: 0.00,
            max: 1000000,
            required: true,
            step: '0.01',
            styleClasses: ['field-label-override-font', 'row', 'full-width-label'],
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            model: 'adjustmentToProjectBudget',
            validator: (value) => (this.validateProjectBudgetAdjustment(value)),
          },
        ],
      };
    },
    getSchema() {
      return {
        id: 'projectBudgetLineItem',
        legend: 'Line Item',
        headerName: 'Line Item',
        fields: [
          {
            type: 'select',
            label: '<span class="label-name">Cost Item</span><br/><span class="help-text">Pick a cost item</span>',
            model: 'product',
            required: true,
            styleClasses: ['field-label-override-font', 'row'],
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            values: this.orderedProducts(),
            onChanged: (model) => {
              const unit = model.product.customfields.find((cf) => cf.fielD_NAME === 'Unit__c');
              if (unit) {
                /* eslint-disable-next-line */
                model.units = unit.fielD_VALUE;
              }
              /* eslint-disable-next-line */
              model.unitCost = null;
              this.$store.dispatch('Project/fetchProductPriceListEntries',
                model.product.producT_ID);
            },
            validator: ['required'],
          },
          {
            type: 'select',
            label: '<span class="label-name">Price List</span><br/><span class="help-text">Pick a price list, or leave blank if this cost item has a non-standard or off-contract unit cost</span>',
            required: false,
            styleClasses: ['field-label-override-font', 'row'],
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            selectOptions: {
              noneSelectedText: 'Select Cost Item Above',
            },
            values: [],
            disabled: true,
            visible: (model) => {
              if (!model.product) {
                return true;
              }
              return false;
            },
          },
          {
            type: 'select',
            label: '<span class="label-name">Price List</span><br/><span class="help-text">Pick a price list, or leave blank if this cost item has a non-standard or off-contract unit cost</span>',
            required: false,
            styleClasses: ['field-label-override-font', 'row'],
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            selectOptions: {
              noneSelectedText: 'Loading...',
            },
            values: [],
            disabled: true,
            visible: (model) => {
              if (model.product && this.currentPriceListsLoading) {
                return true;
              }
              return false;
            },
          },
          {
            type: 'select',
            label: '<span class="label-name">Price List</span><br/><span class="help-text">Pick a price list, or leave blank if this cost item has a non-standard or off-contract unit cost</span>',
            model: 'priceListId',
            required: false,
            styleClasses: ['field-label-override-font', 'row'],
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            values: this.orderedPriceLists(),
            onChanged: (model) => {
              /* eslint-disable-next-line */
              model.priceList = this.currentPriceLists.find((e) => e.pricebooK_ID === model.priceListId);
              /* eslint-disable-next-line */
              if (model.priceList && model.priceList != null && model.priceList.entry && model.priceList.entry.price) {
                /* eslint-disable-next-line */
                model.unitCost = model.priceList.entry.price;
              }
            },
            visible: (model) => {
              if (model.product && !this.currentPriceListsLoading) {
                return true;
              }
              return false;
            },
          },
          {
            type: 'select',
            label: '<span class="label-name">Funding Source</span><br/><span class="help-text">Pick the expected Funding Source for this item</span>',
            model: 'fundingSourceId',
            required: true,
            styleClasses: ['field-label-override-font', 'row'],
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            values: this.orderedFundingSources(),
            validator: ['required'],
          },
          {
            type: 'customDatepicker',
            label: '<span class="label-name">Target Date</span><br/><span class="help-text">Optionally pick a target date for the item to be procured</span>',
            placeholder: 'Target Date',
            model: 'targetDate',
            clearButton: true,
            validator: (value) => {
              if (new Date(value) < this.currentLineItem.fiscalYearStart) return [`Requested Target Date may not be earlier than ${new Date(this.currentLineItem.fiscalYearStart).toLocaleDateString()}`];
              if (new Date(value) >= this.currentLineItem.fiscalYearEnd) return [`Requested Target Date must be earlier than ${new Date(this.currentLineItem.fiscalYearEnd).toLocaleDateString()}`];
              return [];
            },
            styleClasses: ['field-label-override-font', 'row'],
          },
          {
            type: 'input',
            inputType: 'number',
            label: '<span class="label-name">Quantity</span><br/><span class="help-text">Enter the quantity of item (see units below)</span>',
            min: 0.01,
            max: 1000000,
            required: true,
            step: '0.01',
            styleClasses: ['field-label-override-font', 'row'],
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            model: 'quantity',
            validator: (value) => (this.validatePositiveNumber(value, 1000000)),
          },
          {
            type: 'input',
            inputType: 'text',
            label: '<span class="label-name">Units</span>',
            styleClasses: ['field-label-override-font', 'row'],
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            model: 'units',
            disabled: true,
          },
          {
            type: 'input',
            inputType: 'number',
            label: '<span class="label-name">Unit Cost</span><br/><span class="help-text">If no price list was selected, enter the unique unit cost of the item</span>',
            min: 0.01,
            max: 1000000,
            required: true,
            step: '0.01',
            styleClasses: ['field-label-override-font', 'row'],
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            model: 'unitCost',
            validator: (value) => (this.validatePositiveNumber(value, 1000000)),
          },
          {
            type: 'input',
            inputType: 'number',
            label: '<span class="label-name">% District or Partner Cost</span><br/><span class="help-text">Enter the cost share percentage of the item</span>',
            min: 0,
            max: 100,
            required: true,
            step: '1',
            styleClasses: ['field-label-override-font', 'row'],
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            model: 'costShare',
            validator: (value) => (this.validatePositiveWholeNumber(value, 100)),
          },
          {
            type: 'input',
            inputType: 'number',
            label: '<span class="label-name">% Contingency</span><br/><span class="help-text">Enter the contingency percentage of the item</span>',
            min: 0,
            max: 100,
            required: true,
            step: '1',
            styleClasses: ['field-label-override-font', 'row'],
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            model: 'contingencyPercent',
            validator: (value) => (this.validatePositiveWholeNumber(value, 100)),
          },
          {
            type: 'textArea',
            label: '<span class="label-name">Notes</span><br/><span class="help-text">Optionally enter short note for the item</span>',
            model: 'notes',
            max: 100,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: ['field-label-override-font', 'row'],
          },
        ],
      };
    },
    orderedProducts() {
      const types = ['Materials', 'Plant Materials', 'Contracted Services'];
      let orderedProducts = [];
      if (this.products) {
        orderedProducts = this.products.filter(
          (prod) => {
            const field = prod.customfields.find((cf) => cf.fielD_NAME === 'Type__c');
            if (!field) {
              return false;
            }
            return types.includes(field.fielD_VALUE);
          },
        ).map(
          (prod) => ({ id: prod, name: prod.producT_NAME }),
        ).sort(this.nameSort);
      }
      return orderedProducts;
    },
    orderedFundingSources() {
      if (!this.funds || this.funds === null || this.funds.length === 0) return [];
      let fundingSources = [];
      if (this.fundingSources) {
        fundingSources = this.fundingSources.map(
          (fundingSource) => ({ id: fundingSource.recorD_ID, name: fundingSource.recorD_NAME }),
        ).sort(this.nameSort);
      }
      fundingSources = fundingSources.filter((fundingSource) => this.funds.filter((fund) => fund.fundingSourceId === fundingSource.id).length > 0);
      return fundingSources;
    },
    orderedFundingSourcesForCurrentBudget() {
      if (!this.fundingSources || !this.projectBudget) return [];
      // Include funding sources that have line items OR budget adjustments on the project
      const lineItemFundingSourceIds = this.projectBudget.projectBudgetYears
        .flatMap((pby) => pby.projectBudgetLineItems.map((li) => li.fundingSourceId));
      const adjustmentFundingSourceIds = this.projectBudget.projectBudgetYears
        .flatMap((pby) => (pby.annualBudgetIncreases || []).map((ba) => ba.fundingSourceId));
      const fundingSourceIds = [...new Set([...lineItemFundingSourceIds, ...adjustmentFundingSourceIds])];
      return this.fundingSources
        .filter((fs) => fundingSourceIds.includes(fs.recorD_ID))
        .map((fs) => ({ id: fs.recorD_ID, name: fs.recorD_NAME }))
        .sort(this.nameSort);
    },
    orderedPriceLists() {
      if (!this.currentPriceLists) return [];
      return this.currentPriceLists
        .map((pb) => ({ id: pb.pricebooK_ID, name: pb.name }))
        .sort(this.nameSort);
    },
    setExistingFunds(budgetYear) {
      api.get(`/project/fundsPerFiscalYear/${budgetYear.year}`).then((response) => {
        const funds = response.data.map((e) => {
          const fund = { id: e.recorD_ID, name: e.recorD_NAME };
          let field = e.customfields.find((customField) => customField.fielD_NAME === 'Funding_Source__c');
          if (field != null) {
            fund.fundingSourceId = field.fielD_VALUE;
          }
          field = e.customfields.find((customField) => customField.fielD_NAME === 'Fiscal_Year__c');
          if (field != null) {
            fund.fiscalYear = field.fielD_VALUE;
          }
          return fund;
        });
        funds.forEach((e) => this.funds.push(e));
      });
    },
    nameSort(a, b) {
      return a.name.localeCompare(b.name);
    },
  },
};
